import * as THREE from "three";
import type { VFX } from "../../../../../art/vfx.js";
import { WYRM_COLOURS } from "../../rare/wyrmApi";
import { groundAt } from "../terrain";
import type { EnemyView } from "./enemyView";
import { WYRM_CLIPS } from "./wyrmView";

/**
 * The Starfall Wyrm's death explosion, at the crash of its fall (once a season: the showpiece's big moment). Before it
 * the slain Wyrm fell in silence and dissolved where it lay (review 2026-10-23: "it just disappears"); now:
 *
 * - while it falls, glowing cracks spread over its body from the chest and its rim heats white;
 * - on the crash (`WYRM_CLIPS.crash` on its death clock) it bursts: a gold star flash in a lilac halo, a lilac pillar
 *   of light, an upright rose shock ring and a wide rose ring on the ground, opal shards (stretched sparks and crystal
 *   chips) thrown out in lilac, rose and cyan, a cloud of star motes rising, a lingering lilac light, a rose DOM flash
 *   and the boss fall's camera shake — all in saturated opal colours (`colours`), never a white-out;
 * - two after-waves (`waves`): a cyan ring and rose motes, then a last wide lilac ring and gold stars;
 * - the body dissolves through the burst along a wide rose edge (wyrmView.ts `fallDissolveAt` = the crash).
 *
 * All sprites are the pooled sprite VFX (art/vfx.js, hero priority not needed: a few hundred sprites once); the
 * scratch vectors are module-level, so a frame allocates nothing. Reduced effects: the flash, one ring and a tenth of
 * the sprites, no shake, no DOM flash.
 */
export const WYRM_DEATH = {
  /** The after-waves, seconds after the crash on the death clock (wave 0 is the crash itself). */
  waves: [0, 0.12, 0.3],
  /** The cracks reach the whole body by the crash; the rim stays white-hot until then. */
  crackFull: WYRM_CLIPS.crash,
  /** The body's dissolve edge (it runs from the crash for `WYRM_CLIPS.fallDissolveFor`): wide and glowing, in rose. */
  edgeWidth: 0.16, edge: WYRM_COLOURS.rose,
  /**
   * The burst's own colours: the opal's lilac, rose and cyan saturated (the model's pale sheen colours, WYRM_COLOURS,
   * summed under the bloom pass into a white blowout in the first cut) and a warm gold for the star's heart.
   */
  colours: { lilac: 0xa77bff, rose: 0xff86b4, cyan: 0x52d8ff, gold: 0xffc46a },
  /**
   * Sizes (cells; the Wyrm's span is ~3.4 at its scale). The ground rings stay within ~2.7 cells of it: wider, the
   * decal layer laid them over the cliffs and the gorge as pale sheets.
   */
  flash: 1.6, halo: 2.2, shock: 4.0, groundRing: 4.2, ring2: 3.0, ring3: 5.4, pillar: 4.0, pillarWidth: 0.6,
  /** Sprite counts at full quality (the VFX's tier `lod` scales them again). */
  shards: 42, chips: 18, motes: 56, stars: 36,
  /** Camera trauma (a boss's fall: cameraShake.ts Budget.bossDeath) and the DOM flash. */
  trauma: 0.75, screen: { color: "#ffb8e0", peak: 0.22, ms: 420 },
  /** The light it leaves: radius (cells), intensity, seconds. */
  light: { radius: 6, intensity: 3.2, life: 1.6 },
  /** The body's white flash on the crash (a hit's is 0.25 on the Wyrm, WYRM_CLIPS.hitFlash): a kick, not a bleach. */
  bodyFlash: 0.45,
  /** Ring brightness (the decal layer's `bright`): coloured, never white under the bloom. */
  ringBright: 1.2,
  /** Reduced effects: a tenth of the sprites. */
  reducedShare: 0.1,
} as const;

/** The sprite VFX calls the burst makes (the full module at run time; a test or a level without the atlas may lack them). */
export type WyrmDeathFx = Partial<Pick<VFX, "flash" | "ring" | "sparks" | "sparkles" | "light" | "debris" | "beam" | "kick">>;
/** The DOM flash (EnemyViews.beats.screenFlash). */
export type ScreenFlashFn = ((color: string, peak: number, ms: number) => void) | null;

/** The waves of the burst due between two death-clock readings: bit k set when wave k crossed (prev, now]. */
export function wavesDue(prev: number, now: number): number {
  let bits = 0;
  for (let k = 0; k < WYRM_DEATH.waves.length; k++) { const t = WYRM_CLIPS.crash + WYRM_DEATH.waves[k]; if (prev < t && now >= t) bits |= 1 << k; }
  return bits;
}

/** The body's crack reveal (0–1) `dead` seconds into the fall: it spreads over the fall and is whole by the crash. */
export function crackAt(dead: number): number { return Math.max(0, Math.min(1, dead / WYRM_DEATH.crackFull)); }

const LILAC = rgb(WYRM_DEATH.colours.lilac), ROSE = rgb(WYRM_DEATH.colours.rose), CYAN = rgb(WYRM_DEATH.colours.cyan), GOLD = rgb(WYRM_DEATH.colours.gold);
const _c = new THREE.Vector3();
const _g = new THREE.Vector3();
const _top = new THREE.Vector3();

/** One Wyrm's fall at a time (it never shares the field): its death clock last frame, to fire each wave once. */
export class WyrmDeath {
  private view: EnemyView | null = null;
  private last = 0;

  /** Per frame for the dying Wyrm (`v.dead` > 0, its fall or its flee): the cracks, then the burst's waves. */
  frame(v: EnemyView, fx: WyrmDeathFx | null, screenFlash: ScreenFlashFn, reduced: boolean): void {
    if (v !== this.view || v.dead < this.last) {
      this.view = v; this.last = 0;
      if (v.look) { v.look.tintEdge(WYRM_DEATH.edge); v.look.u.uTdEdgeW.value = WYRM_DEATH.edgeWidth; }
    }
    const prev = this.last;
    this.last = v.dead;
    const look = v.look;
    if (look && v.dead < WYRM_DEATH.crackFull) { look.crack = Math.max(look.crack, crackAt(v.dead)); look.heatUp(1); }
    const due = wavesDue(prev, v.dead);
    if (due === 0) return;
    this.where(v);
    if (due & 1) { look?.flash(WYRM_DEATH.bodyFlash); look?.heatUp(1); this.crash(fx, screenFlash, reduced); }
    if (due & 2 && !reduced) this.second(fx);
    if (due & 4 && !reduced) this.third(fx);
  }

  /** Back to the pool: forget the fall. */
  end(v: EnemyView): void { if (v === this.view) { this.view = null; this.last = 0; } }

  /** Its body's middle (`_c`), the ground under it (`_g`) and the pillar's top (`_top`). */
  private where(v: EnemyView): void {
    if (v.torso) v.torso.getWorldPosition(_c);
    else _c.copy(v.root.position).add(v.body.position).setY(v.root.position.y + v.body.position.y + v.height * v.scale * 0.3);
    _g.set(_c.x, groundAt(_c.x, _c.z) + 0.05, _c.z);
    if (_c.y < _g.y + 0.25) _c.y = _g.y + 0.25;
    _top.copy(_g).setY(_g.y + WYRM_DEATH.pillar);
  }

  /** The crash: the star flash, the pillar, the shock rings, the shards and the motes, the light, the shake. */
  private crash(fx: WyrmDeathFx | null, screenFlash: ScreenFlashFn, reduced: boolean): void {
    const D = WYRM_DEATH, k = reduced ? D.reducedShare : 1;
    fx?.flash?.(_c, D.flash, GOLD);
    fx?.ring?.(_g, { size: D.groundRing, life: 0.8, color: ROSE, flat: true, bright: D.ringBright });
    fx?.light?.(_c, LILAC, D.light.radius, D.light.intensity, D.light.life);
    fx?.sparkles?.(_c, Math.round(D.motes * k), { color: LILAC, radius: 1.4, rise: 2.4, life: 1.9, size: 0.18 });
    if (reduced) return;
    fx?.flash?.(_c, D.halo, LILAC);
    fx?.ring?.(_c, { size: D.shock, life: 0.55, color: ROSE, flat: false, bright: D.ringBright });
    fx?.beam?.(_g, _top, { life: 0.6, width: D.pillarWidth, color: D.colours.lilac });
    fx?.sparks?.(_c, D.shards, { color: LILAC, speed: 8, up: 3, gravity: 6, life: 0.9, size: 0.22, floor: _g.y });
    fx?.sparks?.(_c, D.shards * 0.6, { color: ROSE, speed: 6, up: 4, gravity: 7, life: 1.0, size: 0.2, floor: _g.y });
    fx?.sparks?.(_c, D.shards * 0.5, { color: CYAN, speed: 9, up: 2, gravity: 5, life: 0.8, size: 0.18, floor: _g.y });
    fx?.debris?.(_c, D.chips, { speed: 4.5, size: 0.16, color: LILAC });
    fx?.sparkles?.(_c, D.motes * 0.6, { color: CYAN, radius: 1.8, rise: 1.6, life: 1.6, size: 0.14 });
    fx?.kick?.(D.trauma);
    screenFlash?.(D.screen.color, D.screen.peak, D.screen.ms);
  }

  /** The first after-wave: a cyan ring on the ground and rose motes boiling up. */
  private second(fx: WyrmDeathFx | null): void {
    fx?.ring?.(_g, { size: WYRM_DEATH.ring2, life: 0.6, color: CYAN, flat: true, bright: WYRM_DEATH.ringBright });
    fx?.flash?.(_c, WYRM_DEATH.flash, ROSE);
    fx?.sparkles?.(_c, WYRM_DEATH.motes * 0.6, { color: ROSE, radius: 1.1, rise: 2.8, life: 2.0, size: 0.15 });
  }

  /** The last wave: a wide lilac ring rolling out and white stars thrown high. */
  private third(fx: WyrmDeathFx | null): void {
    fx?.ring?.(_g, { size: WYRM_DEATH.ring3, life: 1.0, color: LILAC, flat: true, bright: WYRM_DEATH.ringBright });
    fx?.sparkles?.(_c, WYRM_DEATH.stars, { color: GOLD, radius: 2.2, rise: 3.2, life: 1.5, size: 0.12 });
  }
}

/** 0xRRGGBB → the sprite VFX's [r, g, b] (0–1). */
function rgb(hex: number): [number, number, number] { return [((hex >> 16) & 255) / 255, ((hex >> 8) & 255) / 255, (hex & 255) / 255]; }
